/**
 * 学生のプロフィール項目のbaseとなるkey
 * 複数選択可能な場合、末尾に_listをつける
 *
 * @see https://docs.google.com/spreadsheets/d/1xweHUwr2HtHJ7OzFZacIwbgiv2-MpFFd2OgwQ3OfS4k/edit#gid=1147280921
 */
export type BaseStudentProfileKey =
  | "education_list"
  | "job_hunting_desired_industry"
  | "job_hunting_desired_occupation"
  | "job_hunting_core_values_first"
  | "job_hunting_core_values_second"
  | "job_hunting_core_values_third"
  | "job_hunting_desired_region"
  | "job_hunting_desired_company_type"
  | "did_your_best"
  | "strengths_and_weaknesses"
  | "want_to_do"
  | "experiences_in_school_days"
  | "years_of_programming_experience"
  | "years_of_design_experience"
  | "long_internship_experience_list"
  | "short_internship_experience_list"
  | "overseas_internship_experience_list"
  | "study_abroad_experience_list"
  | "project_experience_list"
  | "starting_business_experience_list"
  | "sport_experience_list"
  | "seminar_experience_list"
  | "part_time_job_experience_list"
  | "programming_experience_list"
  | "design_experience_list"
  | "achievement"
  | "related_link_list"
  | "foreign_language_list"
  | "qualification_list"
  | "skill"
  | "badges_achievement"

/**
 * 文字列型の末尾に_listがあったら.${number}をつけた文字列型に変換する
 * @example
 * ```ts
 * type Sample = "apple" | "orange_list"
 * type Result = Converter<Sample>
 * // Result is "apple" | `orange.${number}` | "orange."
 * ```
 */
type Converter<S extends string> = S extends `${infer Key}_list` ? `${Key}.${number | ""}` : S

/**
 *  学生のプロフィール項目のkey
 * @example
 * ```ts
 * const sample: StudentProfileKey = `education.${0}`
 * ```
 */
export type StudentProfileKey = Converter<BaseStudentProfileKey>

const STUDENT_PROFILE_DESCRIPTION_MAP: Record<BaseStudentProfileKey, string> = {
  education_list: "学歴",
  job_hunting_desired_industry: "興味のある業界",
  job_hunting_desired_occupation: "興味のある職種",
  job_hunting_core_values_first: "就職活動の軸（第1優先）",
  job_hunting_core_values_second: "就職活動の軸（第2優先）",
  job_hunting_core_values_third: "就職活動の軸（第3優先）",
  job_hunting_desired_region: "就職希望エリア",
  job_hunting_desired_company_type: "希望する企業規模",
  did_your_best: "学生時代に力を入れたこと",
  strengths_and_weaknesses: "あなたの強みや弱み",
  want_to_do: "この先やりたいこと",
  experiences_in_school_days: "学生時代に行ったこと",
  years_of_programming_experience: "プログラミングの経験年数",
  years_of_design_experience: "デザインの経験年数",
  long_internship_experience_list: "長期インターンの経験",
  short_internship_experience_list: "短期インターンの経験",
  overseas_internship_experience_list: "海外インターンの経験",
  study_abroad_experience_list: "留学の経験",
  project_experience_list: "学生団体・ボランティア・サークルの経験",
  starting_business_experience_list: "起業の経験",
  sport_experience_list: "体育会の経験",
  seminar_experience_list: "ゼミ・研究室の経験",
  part_time_job_experience_list: "アルバイトの経験",
  programming_experience_list: "プログラミングの経験",
  design_experience_list: "デザインの経験",
  achievement: "学生時代の実績",
  related_link_list: "実績の分かるURL",
  foreign_language_list: "外国語スキル",
  qualification_list: "資格",
  skill: "扱えるツールや言語、特徴",
  badges_achievement: "実績バッジ",
}

const REGEX = /\..*$/

export const toBaseStudentProfileKey = (key: StudentProfileKey): BaseStudentProfileKey => {
  if (REGEX.test(key)) {
    const resultKey = key.replace(REGEX, "") + "_list"
    return resultKey as BaseStudentProfileKey
  } else {
    return key as BaseStudentProfileKey
  }
}

export const getStudentProfileDescription = (key: StudentProfileKey): string => {
  return STUDENT_PROFILE_DESCRIPTION_MAP[toBaseStudentProfileKey(key)]
}
